/* eslint-disable react-hooks/exhaustive-deps */

import StreamingIndicator from "components/StreamingIndicator/StreamingIndicator";
import ConsensusMeter from "features/ConsensusMeter/ConsensusMeter";
import { Paper, SearchIdParams } from "helpers/api";
import React, { useCallback, useMemo } from "react";
import Caption, { CaptionMarkdown } from "../components/Caption";
import CoFeature, {
  CoFeatureMarkdown,
  CoFeaturePlaceholder,
} from "../components/CoFeature";
import EvidenceStrengthIndicator, {
  EvidenceStrengthIndicatorExport,
  EvidenceStrengthIndicatorMarkdown,
  EvidenceStrengthIndicatorPlaceholder,
} from "../components/EvidenceStrengthIndicator";
import KeyPaperPreview, {
  KeyPaperPreviewMarkdown,
} from "../components/KeyPaperPreview";
import OpenResearchQuestion, {
  OpenResearchQuestionMarkdown,
  OpenResearchQuestionPlaceholder,
} from "../components/OpenResearchQuestion";
import Citation, { CitationMarkdown } from "../components/Citation";
import { CitationAdapter } from "../components/CitationAdapter";
import ResearchGapHeatmapCell, {
  ResearchGapHeatmapCellMarkdown,
} from "../components/ResearchGapHeatmapCell";
import SearchStrategyDiagram, {
  SearchStrategyDiagramExport,
  SearchStrategyDiagramMarkdown,
} from "../components/SearchStrategyDiagram";
import TextPlaceholder from "../components/TextPlaceholder";
import TopContributors, {
  TopContributorsMarkdown,
  TopContributorsPlaceholder,
} from "../components/TopContributors";
import {
  createComponentRegistry,
  createRegistryEntry,
  processMarkdownWithConverter,
} from "../plugins/react-markdown-registry/utils";

export interface useComponentRegistryProps {
  papers?: Paper[];
  searchIdParams?: SearchIdParams;
  threadId?: string;
  onClickCitation?: (
    paper: Paper,
    paperIndex: number,
    referenceLineNumber: number,
    associatedSentence: string,
    quote?: string,
    quoteIndex?: number
  ) => void;
  onSelectExcerpt?: (text: string, paper_id: string) => void;
  onCitationFoundInAnalysis?: (
    paperIndex: number,
    quote?: string,
    quoteIndex?: number
  ) => void;
  interactionId?: string;
  isProResearch?: boolean;
  showConsensusMeterLoadingState?: boolean; // Whether to show the consensus meter loading state
  originalUserMessage?: string; // The original user message that was used to generate the analysis
  isConsensusMeterFirst?: boolean; // Whether consensus meter is the first element in the markdown
  fullTextPaperIds?: string[]; // List of paper IDs that used full text
}

export enum VALID_CONVERTERS {
  copyTextMarkdown = "copy-text-markdown",
  exportMarkdown = "export-markdown",
}

export enum VALID_MODES {
  export = "export",
}

const useComponentRegistry = ({
  papers = [],
  searchIdParams,
  threadId,
  onClickCitation,
  onSelectExcerpt,
  onCitationFoundInAnalysis,
  interactionId,
  isProResearch = false,
  showConsensusMeterLoadingState = true,
  originalUserMessage = undefined,
  isConsensusMeterFirst = false,
  fullTextPaperIds = [],
}: useComponentRegistryProps) => {
  // Memoize callback functions to prevent components object from changing
  const memoizedOnClickCitation = useCallback(onClickCitation ?? (() => {}), [
    onClickCitation,
  ]);

  const memoizedOnSelectExcerpt = useCallback(
    (text: string, paper_id: string) => {
      onSelectExcerpt?.(text, paper_id);
    },
    [onSelectExcerpt]
  );

  const memoizedOnCitationFoundInAnalysis = useCallback(
    (paperIndex: number, quote?: string, quoteIndex?: number) => {
      onCitationFoundInAnalysis?.(paperIndex, quote, quoteIndex);
    },
    [onCitationFoundInAnalysis]
  );

  // Purposefully not using modes here because we do not want to show the consensus meter in the export mode
  const consensusMeterRegistryEntry = useMemo(
    () =>
      createRegistryEntry({
        defaultComponent: ConsensusMeter,
        markdownPropKeys: ["generatedQueryOverride"],
        additionalPropValues: {
          searchParams: {
            searchId: searchIdParams?.threadSearchId,
            isProResearch: isProResearch,
          },
          threadInteractionId: interactionId,
          threadId: threadId,
          markdownEmbedded: true,
          showLoadingState: showConsensusMeterLoadingState,
          originalUserMessage: originalUserMessage,
          isFirstElement: isConsensusMeterFirst,
        },
        converters: {
          [VALID_CONVERTERS.copyTextMarkdown]: () => {
            return "";
          },
        },
      }),
    [isConsensusMeterFirst]
  );

  const captionRegistryEntry = useMemo(
    () =>
      createRegistryEntry({
        defaultComponent: Caption,
        modes: {
          [VALID_MODES.export]: Caption,
        },
        markdownPropKeys: ["number", "description"],
        converters: {
          [VALID_CONVERTERS.copyTextMarkdown]: CaptionMarkdown,
        },
      }),
    []
  );

  const evidenceStrengthIndicatorRegistryEntry = useMemo(
    () =>
      createRegistryEntry({
        defaultComponent: EvidenceStrengthIndicator,
        modes: {
          [VALID_MODES.export]: EvidenceStrengthIndicatorExport,
        },
        markdownPropKeys: ["number"],
        placeholder: EvidenceStrengthIndicatorPlaceholder,
        converters: {
          [VALID_CONVERTERS.copyTextMarkdown]:
            EvidenceStrengthIndicatorMarkdown,
        },
      }),
    []
  );

  const topContributorsRegistryEntry = useMemo(
    () =>
      createRegistryEntry({
        defaultComponent: TopContributors,

        markdownPropKeys: ["authors", "journals", "institutions"],
        additionalPropValues: {
          papers: papers,
          onClickCitation: memoizedOnClickCitation,
        },
        placeholder: TopContributorsPlaceholder,
        converters: {
          [VALID_CONVERTERS.copyTextMarkdown]: TopContributorsMarkdown,
          [VALID_CONVERTERS.exportMarkdown]: TopContributorsMarkdown,
        },
      }),
    []
  );

  const researchGapHeatmapCellRegistryEntry = useMemo(
    () =>
      createRegistryEntry({
        defaultComponent: ResearchGapHeatmapCell,
        modes: {
          [VALID_MODES.export]: ResearchGapHeatmapCell,
        },
        markdownPropKeys: ["number"],
        additionalPropValues: {
          threadInteractionId: interactionId,
        },
        converters: {
          [VALID_CONVERTERS.copyTextMarkdown]: ResearchGapHeatmapCellMarkdown,
        },
      }),
    []
  );

  const searchStrategyDiagramRegistryEntry = useMemo(
    () =>
      createRegistryEntry({
        defaultComponent: SearchStrategyDiagram,
        modes: {
          [VALID_MODES.export]: SearchStrategyDiagramExport,
        },
        markdownPropKeys: [],
        additionalPropValues: {
          threadInteractionId: interactionId,
        },
        placeholder: TextPlaceholder,
        converters: {
          [VALID_CONVERTERS.copyTextMarkdown]: SearchStrategyDiagramMarkdown,
        },
      }),
    []
  );

  const paperItemRegistryEntry = useMemo(
    () =>
      createRegistryEntry({
        defaultComponent: KeyPaperPreview,
        markdownPropKeys: ["originalIndex"],
        additionalPropValues: {
          papers: papers,
        },
        placeholder: StreamingIndicator,
        converters: {
          [VALID_CONVERTERS.copyTextMarkdown]: KeyPaperPreviewMarkdown,
          [VALID_CONVERTERS.exportMarkdown]: KeyPaperPreviewMarkdown, // Convert to markdown for citation formatting compatibility
        },
      }),
    [papers]
  );

  const openResearchQuestionRegistryEntry = useMemo(
    () =>
      createRegistryEntry({
        defaultComponent: OpenResearchQuestion,
        markdownPropKeys: ["question", "mode"],
        additionalPropValues: {
          threadId: threadId,
        },
        placeholder: OpenResearchQuestionPlaceholder,
        converters: {
          [VALID_CONVERTERS.copyTextMarkdown]: OpenResearchQuestionMarkdown,
          [VALID_CONVERTERS.exportMarkdown]: OpenResearchQuestionMarkdown,
        },
      }),
    []
  );

  const paperCiteRegistryEntry = useMemo(
    () =>
      createRegistryEntry({
        defaultComponent: CitationAdapter,
        markdownPropKeys: ["paperIndex", "paper_id", "quote", "quoteIndex"],
        additionalPropValues: {
          papers: papers,
          onClick: memoizedOnClickCitation,
          onCitationFoundInAnalysis: memoizedOnCitationFoundInAnalysis,
          fullTextPaperIds: fullTextPaperIds,
        },
        placeholder: StreamingIndicator,
        converters: {
          [VALID_CONVERTERS.copyTextMarkdown]: CitationMarkdown,
          [VALID_CONVERTERS.exportMarkdown]: CitationMarkdown,
        },
      }),
    [papers, memoizedOnCitationFoundInAnalysis, fullTextPaperIds]
  );

  const coFeatureRegistryEntry = useMemo(
    () =>
      createRegistryEntry({
        defaultComponent: CoFeature,
        markdownPropKeys: ["feature"],
        additionalPropValues: {},
        placeholder: CoFeaturePlaceholder,
        converters: {
          [VALID_CONVERTERS.copyTextMarkdown]: CoFeatureMarkdown,
          [VALID_CONVERTERS.exportMarkdown]: CoFeatureMarkdown,
        },
      }),
    []
  );

  const componentRegistry = useMemo(() => {
    return createComponentRegistry({
      consensus_meter: consensusMeterRegistryEntry, // <consensus_meter /> => ConsensusMeter
      co_feature: coFeatureRegistryEntry, // <co_feature feature="pro_analysis" /> => CoFeature
      figure_caption: captionRegistryEntry, // <figure_caption number="1" description="This is a caption" /> => Caption
      evidence_strength_indicator: evidenceStrengthIndicatorRegistryEntry, // <evidence_strength_indicator number="1" /> => EvidenceStrengthIndicator
      top_contributors: topContributorsRegistryEntry, // <top_contributors authors={[...]} journals={[...]} /> => TopContributors
      research_gap_heatmap_cell: researchGapHeatmapCellRegistryEntry, // <research_gap_heatmap_cell number="1" maxValue="10" /> => ResearchGapHeatmapCell
      search_strategy_diagram: searchStrategyDiagramRegistryEntry, // <search_strategy_diagram identifiedPapers="N" screenedPapers="X" eligiblePapers="Y" includedPapers="Z" /> => SearchStrategyDiagram
      paper_item: paperItemRegistryEntry, // <paper_item originalIndex="1" /> => KeyPaperPreview
      paper_cite: paperCiteRegistryEntry, // <paper_cite paper_id="..." quote="..." /> => Citation
      open_research_question: openResearchQuestionRegistryEntry, // <open_research_question question="What is the effect of..." /> => OpenResearchQuestion
    });
  }, [
    consensusMeterRegistryEntry,
    paperItemRegistryEntry,
    paperCiteRegistryEntry,
  ]);

  const processAnalysisForCopyText = (analysis: string) => {
    return processMarkdownWithConverter(
      analysis,
      componentRegistry,
      VALID_CONVERTERS.copyTextMarkdown
    );
  };

  const processAnalysisForExport = (analysis: string) => {
    return processMarkdownWithConverter(
      analysis,
      componentRegistry,
      VALID_CONVERTERS.exportMarkdown
    );
  };

  return {
    componentRegistry,
    processAnalysisForCopyText,
    processAnalysisForExport,
  };
};

export default useComponentRegistry;
